/**
 * Castings (BAL-1205): an `auditions` row with `opportunity_kind = 'job'`.
 *
 * A job is paid work (a Fever show), an audition is a selection for a company
 * or school. The two share every table, form and apply flow; only the listing
 * (/castings) and the pay/roles block tell them apart.
 */

export const OPPORTUNITY_KINDS = ['audition', 'job'] as const;
export type OpportunityKind = (typeof OPPORTUNITY_KINDS)[number];

/** Mirrors `auditions_compensation_unit_check`. */
export const COMPENSATION_UNITS = [
  'show',
  'day',
  'week',
  'month',
  'project',
] as const;
export type CompensationUnit = (typeof COMPENSATION_UNITS)[number];

export interface CompensationFields {
  compensation_amount?: number | string | null;
  compensation_currency?: string | null;
  compensation_unit?: string | null;
}

export function isCasting(row: { opportunity_kind?: string | null }): boolean {
  return row.opportunity_kind === 'job';
}

function isCompensationUnit(value: unknown): value is CompensationUnit {
  return (
    typeof value === 'string' &&
    (COMPENSATION_UNITS as readonly string[]).includes(value)
  );
}

/**
 * The pay to display, or null when there is none to show. PostgREST returns
 * `numeric` as a number, but a string survives a JSON round-trip through a
 * cache, so both are accepted. An amount without a currency is not shown: the
 * DB refuses that row, and a bare number would mislead.
 */
export function resolveCompensation(row: CompensationFields): {
  amount: number;
  currency: string;
  unit: CompensationUnit | null;
} | null {
  const raw = row.compensation_amount;
  if (raw === null || raw === undefined || raw === '') return null;
  const amount = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(amount) || amount < 0) return null;
  const currency = row.compensation_currency?.trim().toUpperCase();
  if (!currency || !/^[A-Z]{3}$/.test(currency)) return null;
  return {
    amount,
    currency,
    unit: isCompensationUnit(row.compensation_unit)
      ? row.compensation_unit
      : null,
  };
}

/** "CHF 250" / "250,00 €" — locale-aware, no decimals when the amount is whole. */
export function formatCompensationAmount(
  compensation: { amount: number; currency: string },
  locale: string,
): string {
  const whole = Number.isInteger(compensation.amount);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: compensation.currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(compensation.amount);
}

/** schema.org `QuantitativeValue.unitText` accepts HOUR, DAY, WEEK, MONTH, YEAR. */
const SCHEMA_ORG_UNIT: Partial<Record<CompensationUnit, string>> = {
  day: 'DAY',
  week: 'WEEK',
  month: 'MONTH',
};

/**
 * The `salary` input of `generateJobPostingSchema` (Google for Jobs
 * `baseSalary`). Only for units schema.org can express: a per-show or
 * per-project fee has no `unitText`, and a `baseSalary` without one is
 * flagged as invalid structured data.
 */
export function jobPostingSalary(
  row: CompensationFields,
): { amount: number; currency: string; unit: string } | null {
  const compensation = resolveCompensation(row);
  const unit = compensation?.unit ? SCHEMA_ORG_UNIT[compensation.unit] : null;
  if (!compensation || !unit) return null;
  return { amount: compensation.amount, currency: compensation.currency, unit };
}

/** Trimmed, de-duplicated, non-empty role names in their original order. */
export function normalizeRoles(roles: readonly string[] | null | undefined) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const role of roles ?? []) {
    const name = role.trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}
